import { SubstepType } from "~/Configurators/Linear/dtos";
import { isNonNull, toMapFn } from "~/utils/reducers";
import type { SummaryData, SummaryDataProvider } from "~/Configurators/Shaft/SummaryDataProvider";
import { removeMultipleTags } from "~/utils/domUtils";
import { InputCstics } from "~/Configurators/Rail/components/ConfigureRailSubstep/constants";
import { getMainKmat } from "~/Configurators/Shaft/common/commonShaftHelpers";
import { formatCsticValueAsNumber } from "~/Configurators/Shaft/components/useMinMaxCsticLabel";
import { getMainKmatCstics } from "~/Configurators/common/v2/helper";

export function getStep1SummaryData(summaryProvider: SummaryDataProvider): SummaryData[] {
  const substeps = [SubstepType.Series, SubstepType.Size];
  return prepareSubStepsInfo(summaryProvider, substeps) ?? [];
}

export function getStep2SummaryData(summaryProvider: SummaryDataProvider) {
  const substeps = [SubstepType.Coating, SubstepType.Accuracy];
  return prepareSubStepsInfo(summaryProvider, substeps);
}

export function getStep3SummaryData(summaryProvider: SummaryDataProvider) {
  const substeps = [SubstepType.Mounting, SubstepType.Closure];
  return prepareSubStepsInfo(summaryProvider, substeps);
}

function prepareSubStepsInfo(summaryProvider: SummaryDataProvider, substeps: SubstepType[]) {
  const substepData = substeps
    .map((substepType) => [substepType, summaryProvider.prepareTileSubstepSummary(substepType)] as const)
    .filter(([, summary]) => isNonNull(summary)) as [SubstepType, SummaryData][];

  if (substepData.length === 0) {
    return null;
  }

  substepData.forEach((data) => {
    data[1].value = removeMultipleTags(data[1].value, "p", "span")!;
    data[1].name = removeMultipleTags(data[1].name, "p", "span")!;
  });

  const data: Record<string, SummaryData> = toMapFn(
    substepData,
    ([type]) => type,
    ([, summary]) => summary,
  );

  return substeps.map((stepName) => data[stepName]).filter(Boolean) as SummaryData[];
}

export function getStep4SummaryData(summaryProvider: SummaryDataProvider): SummaryData[] {
  const substeps = [SubstepType.LocatingFace];
  const t = summaryProvider.t;
  const summaryData: SummaryData[] = substeps
    .map((substepType) => summaryProvider.prepareTileSubstepSummary(substepType))
    .filter(isNonNull);

  const configureRailSubstep = summaryProvider.substeps[SubstepType.ConfigureRail];
  if (configureRailSubstep) {
    const inputCstics = getMainKmatCstics(configureRailSubstep).input;
    const lengthDef = inputCstics[InputCstics.TotalLength];
    const alDef = inputCstics[InputCstics.aL];
    const arDef = inputCstics[InputCstics.aR];
    const partSizingDef = inputCstics[InputCstics.partSizingType];
    const partAmountDef = inputCstics[InputCstics.partAmount];

    [lengthDef, alDef, arDef]
      .filter(Boolean)
      .map((def) => {
        const cstics = getMainKmat({ kmats: summaryProvider.kmats }).cstics;
        return cstics[def.name];
      })
      .filter((cstic) => cstic?.value)
      .forEach((cstic) => {
        summaryData.push({
          name: t(cstic.label),
          isStandard: false,
          value: formatNumberValue(cstic.value, "mm"),
        });
      });

    [partSizingDef]
      .filter(Boolean)
      .map((def) => {
        const cstics = getMainKmat({ kmats: summaryProvider.kmats }).cstics;
        return cstics[def.name];
      })
      .filter((cstic) => cstic?.value)
      .forEach((cstic) => {
        summaryData.push({
          name: t(cstic.label),
          isStandard: false,
          value: t(cstic.values.find((v) => v.value === cstic.value)?.label ?? ""),
        });
      });

    [partAmountDef]
      .filter(Boolean)
      .map((def) => {
        const cstics = getMainKmat({ kmats: summaryProvider.kmats }).cstics;
        return cstics[def.name];
      })
      .filter((cstic) => cstic?.value)
      .forEach((cstic) => {
        summaryData.push({
          name: t(cstic.label),
          isStandard: false,
          value: t(cstic.values.find((v) => v.value === cstic.value)?.label ?? ""),
        });
      });
  }

  summaryData.forEach((data) => {
    data.value = removeMultipleTags(data.value, "p", "span")!;
    data.name = removeMultipleTags(data.name, "p", "span")!;
  });

  return summaryData;
}

function formatNumberValue(value: string | undefined, unit?: string) {
  let val = formatCsticValueAsNumber(value, 2);
  if (unit) {
    val += unit;
  }
  return val;
}
